import { useDisclosure, useTheme } from '@chakra-ui/react';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import Button from '~/common/components/Button';
import Modal from '~/common/components/Modals/Modal';
import ModalBody from '~/common/components/Modals/ModalBody';
import ModalCloseButton from '~/common/components/Modals/ModalCloseButton';
import ModalContent from '~/common/components/Modals/ModalContent';
import ModalFooter from '~/common/components/Modals/ModalFooter';
import ModalHeader from '~/common/components/Modals/ModalHeader';
import ModalOverlay from '~/common/components/Modals/ModalOverlay';
import Text from '~/common/components/Texts/Text';
import { API_STATUS } from '~/common/constants';
import { pixelsToRem } from '~/theme/typography';

type ConfirmationDialogProps = {
  status?: any;
  title?: string;
  isDeleteDisabled?: boolean;
  message?: string | number | React.ReactNode;
  itemName?: string;
  onClick?: (...args: any[]) => any;
  onClose?: () => void;
  open?: boolean;
  children?: React.ReactNode;
  buttonId?: string;
  openFromExternalMethod?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

function RenderModal({
  open,
  onClose,
  title,
  message,
  itemName,
  isDisabled,
  handleConfirmation,
  size = 'md',
}) {
  return (
    <Modal isOpen={open} onClose={onClose} size={size}>
      <ModalOverlay />
      <ModalContent p={6}>
        {!!title && (
          <ModalHeader
            textTransform="capitalize"
            sx={{
              fontSize: '1rem',
              fontWeight: 500,
              marginRight: 6,
              paddingTop: 0,
              paddingLeft: 0,
            }}
          >
            {title}
          </ModalHeader>
        )}

        <ModalCloseButton
          onClick={onClose}
          data-testid="hideDocModalClose"
          sx={{
            position: 'absolute',
            top: '15px',
            right: '14px',
            ':hover': { bgcolor: 'transparent', cursor: 'pointer' },
            ':active': { bgcolor: 'transparent' },
            ':focus': { bgcolor: 'transparent' },
          }}
        />

        <ModalBody p="unset">
          <Text
            fontSize={pixelsToRem(14)}
            textAlign={title ?? 'center'}
            mt={title ?? 5}
          >
            {message}
          </Text>
        </ModalBody>

        <ModalFooter
          p="unset"
          mt="24px"
          justifyContent="space-between"
          textTransform="capitalize"
        >
          <Button
            disabled={isDisabled}
            onClick={onClose}
            data-testid="hideDocModalNo"
            id="hideDocModalNo"
            isPrimary={false}
            aria-label={`No, Keep ${itemName}`}
          >
            No, Keep {itemName}
          </Button>

          <Button
            disabled={isDisabled}
            onClick={handleConfirmation}
            data-testid="hideDocModalYes"
            id="hideDocModalYes"
            aria-label={`Yes, Remove ${itemName}`}
          >
            Yes, Remove {itemName}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const ConfirmationDialog = ({
  onClick,
  title,
  message,
  itemName,
  status,
  openFromExternalMethod = false,
  children,
  buttonId,
  isDeleteDisabled = false,
  onClose,
  size = 'md',
}: Readonly<ConfirmationDialogProps>) => {
  const { isOpen, onClose: hookOnClose, onOpen } = useDisclosure();
  const { colors } = useTheme();

  const handleConfirmation = () => {
    onClick();
    if (openFromExternalMethod) {
      onClose();
    } else {
      hookOnClose();
    }
  };

  const isDisabled =
    status === API_STATUS.GETTING || status === API_STATUS.UPDATING;

  if (openFromExternalMethod) {
    return RenderModal({
      open: true,
      onClose,
      title,
      message,
      itemName,
      isDisabled,
      handleConfirmation,
      size,
    });
  }

  return (
    <>
      <Button
        aria-label="openDeleteModalButton"
        backgroundColor={'transparent'}
        _hover={{ bg: 'transparent', cursor: 'pointer' }}
        _active={{ bg: 'transparent' }}
        _focus={{ bg: 'transparent' }}
        onClick={() => onOpen()}
        px={0}
        dataTestid={buttonId}
        isDisabled={isDeleteDisabled}
      >
        {children ?? (
          <FontAwesomeIcon icon={faTrash} color={`${colors?.red['600']}`} />
        )}
      </Button>
      {RenderModal({
        open: isOpen,
        onClose: hookOnClose,
        title,
        message,
        itemName,
        isDisabled,
        handleConfirmation,
        size,
      })}
    </>
  );
};

export default ConfirmationDialog;
